import React from 'react';
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Grid,
  Divider,
  Paper,
} from '@mui/material';
import {Delete as DeleteIcon} from '@mui/icons-material';
import dayjs from 'dayjs';
import {StatusChip} from './UrlaubsantragStatusChip';
import { MeineAntraegeTabProps } from '../../../../Contexts/Urlaubsantrag/UrlaubsantragContext';

export const MeineAntraegeTab: React.FC<MeineAntraegeTabProps> = ({loading, antraege}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Format date for display
  const formatDate = (dateString: string) => {
    return dayjs(dateString).format('DD.MM.YYYY');
  };

  if (loading) {
    return (
      <Box sx={{display: 'flex', justifyContent: 'center', p: 3}}>
        <CircularProgress />
      </Box>
    );
  }

  if (antraege.length === 0) {
    return (
      <Box sx={{p: 2}}>
        <Typography variant='body1'>Keine Urlaubsanträge vorhanden</Typography>
      </Box>
    );
  }

  // Mobile layout with cards
  if (isMobile) {
    return (
      <Box sx={{p: 1}}>
        {antraege.map((antrag) => (
          <Paper
            key={antrag.id}
            elevation={2}
            sx={{
              mb: 2,
              overflow: 'hidden',
              borderRadius: 1,
            }}>
            <Box
              sx={{
                p: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                bgcolor: 'rgba(0, 0, 0, 0.03)',
              }}>
              <Typography variant='subtitle1' fontWeight='medium'>
                {formatDate(antrag.beginn)} - {formatDate(antrag.ende)}
              </Typography>
              <StatusChip status={antrag.status} />
            </Box>
            <Divider />
            <Box sx={{p: 2}}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant='body2' color='text.secondary'>
                    Grund:
                  </Typography>
                  <Typography variant='body1'>{antrag.grund || '-'}</Typography>
                </Grid>
                {antrag?.notiz && (
                  <Grid item xs={12}>
                    <Typography variant='body2' color='text.secondary'>
                      Notizen:
                    </Typography>
                    <Typography variant='body1'>{antrag.notiz}</Typography>
                  </Grid>
                )}
                {antrag.bearbeitung_datum && (
                  <Grid item xs={12}>
                    <Typography variant='body2' color='text.secondary'>
                      Bearbeitet am:
                    </Typography>
                    <Typography variant='body1'>{formatDate(antrag.bearbeitung_datum)}</Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          </Paper>
        ))}
      </Box>
    );
  }

  // Desktop layout with table
  return (
    <TableContainer sx={{overflowX: 'auto'}}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{fontWeight: 'bold'}}>Von</TableCell>
            <TableCell sx={{fontWeight: 'bold'}}>Bis</TableCell>
            <TableCell sx={{fontWeight: 'bold'}}>Grund</TableCell>
            <TableCell sx={{fontWeight: 'bold'}}>Notizen</TableCell>
            <TableCell sx={{fontWeight: 'bold'}}>Status</TableCell>
            <TableCell sx={{fontWeight: 'bold'}}>Bearbeitet am</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {antraege.map((antrag) => (
            <TableRow key={antrag.id}>
              <TableCell>{formatDate(antrag.beginn)}</TableCell>
              <TableCell>{formatDate(antrag.ende)}</TableCell>
              <TableCell>{antrag.grund || '-'}</TableCell>
              <TableCell>{antrag?.notiz || '-'}</TableCell>
              <TableCell>
                <StatusChip status={antrag.status} />
              </TableCell>
              <TableCell>
                {antrag.bearbeitung_datum ? formatDate(antrag.bearbeitung_datum) : '-'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
